'use client';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { getSeePlansRoute, getStartSubscriptionRoute, getUnlimitedRequestsRoute } from '@/lib/ctaRouting';

interface ServiceCategory {
  id: number
  title: string
  services: string[]
}

interface WhatWeDoSectionProps {
  serviceCategories: ServiceCategory[]
  loading: boolean
}

export default function WhatWeDoSection({ serviceCategories, loading }: WhatWeDoSectionProps) {
  const router = useRouter();

  const handleSeePlans = async () => {
    const route = await getSeePlansRoute();
    router?.push(route);
    window.scrollTo(0, 0);
  };

  const handleStartSubscription = async () => {
    const route = await getStartSubscriptionRoute();
    router?.push(route);
    window.scrollTo(0, 0);
  };

  const handleGetStarted = async () => {
    const route = await getUnlimitedRequestsRoute();
    router?.push(route);
    window.scrollTo(0, 0);
  };

  return (
    <>
      {/* What Purple Cow Actually Is Section */}
      <section className="w-full bg-white py-12 sm:py-16 lg:py-24">
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-20 xl:px-36">
          {/* Header */}
          <div className="flex flex-col justify-center items-center mb-10 sm:mb-14 lg:mb-20">
            <h2 className="text-[36px] sm:text-[56px] lg:text-[80px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651]">
              What Purple Cow
            </h2>
            <h3 className="text-[36px] sm:text-[56px] lg:text-[80px] font-bebas font-normal leading-[1.1] text-center text-[#3f1651] -mt-1 sm:-mt-2 lg:-mt-3">
              Unlimited Actually Is
            </h3>
          </div>

          {/* Content Grid */}
          <div className="flex flex-col lg:flex-row justify-between items-start gap-10 lg:gap-16 mb-10 sm:mb-14 lg:mb-16">
            {/* Left Content */}
            <div className="w-full lg:w-[45%]">
              <p className="text-[18px] sm:text-[20px] lg:text-[24px] font-poppins font-medium leading-[1.4] text-[#3f1651] mb-4 sm:mb-6">
                Purple Cow Unlimited is your always-on creative and marketing engine.
              </p>
              
              <p className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal leading-[1.6] text-[#666666]">
                It's the simplest way for any business leader to finally get consistent creative output every single week.
              </p>
            </div>

            {/* Right Steps */}
            <div className="w-full lg:w-[50%] space-y-4 sm:space-y-5 lg:space-y-6">
              {/* Step 1 */}
              <div className="flex items-start gap-4 sm:gap-5 p-4 sm:p-5 lg:p-6 border border-[#e5e5e5] rounded-xl">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#8923b3] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[16px] sm:text-[18px] font-bebas">1</span>
                </div>
                <div>
                  <h4 className="text-[16px] sm:text-[18px] lg:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-1">
                    You Submit a Request
                  </h4>
                  <p className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal text-[#666666]">
                    Add tasks to your queue anytime
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex items-start gap-4 sm:gap-5 p-4 sm:p-5 lg:p-6 border border-[#e5e5e5] rounded-xl">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#8923b3] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[16px] sm:text-[18px] font-bebas">2</span>
                </div>
                <div>
                  <h4 className="text-[16px] sm:text-[18px] lg:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-1">
                    We Work Daily
                  </h4>
                  <p className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal text-[#666666]">
                    Your team is always making progress
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex items-start gap-4 sm:gap-5 p-4 sm:p-5 lg:p-6 border border-[#e5e5e5] rounded-xl">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#8923b3] flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[16px] sm:text-[18px] font-bebas">3</span>
                </div>
                <div>
                  <h4 className="text-[16px] sm:text-[18px] lg:text-[20px] font-bebas font-normal text-[#3f1651] uppercase mb-1">
                    You Review, Revise, and Approve
                  </h4>
                  <p className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal text-[#666666]">
                    Unlimited revisions until it's perfect
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Gray Card */}
          <div className="bg-[#f5f3f7] rounded-2xl p-6 sm:p-8 lg:p-10">
            {/* Three X Items */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-12 lg:gap-20 mb-8 sm:mb-10">
              <div className="flex flex-col items-center gap-2">
                <span className="text-[#8923b3] text-[24px] sm:text-[28px]">✕</span>
                <span className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal text-[#3f1651]">No hours to track</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-[#8923b3] text-[24px] sm:text-[28px]">✕</span>
                <span className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal text-[#3f1651]">No unpredictable fees</span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-[#8923b3] text-[24px] sm:text-[28px]">✕</span>
                <span className="text-[14px] sm:text-[15px] lg:text-[16px] font-poppins font-normal text-[#3f1651]">No micromanagement</span>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
              <button
                onClick={handleSeePlans}
                className="w-full sm:w-auto px-8 py-4 bg-[#8923b3] text-white text-xs font-medium uppercase tracking-wider rounded-full hover:bg-[#a035d1] transition-colors"
              >
                SEE THE PLANS
              </button>

              <button
                onClick={handleStartSubscription}
                className="w-full sm:w-auto px-8 py-4 bg-transparent text-[#8923b3] text-xs font-medium uppercase tracking-wider rounded-full border-2 border-[#8923b3] hover:bg-[#8923b3] hover:text-white transition-colors"
              >
                START YOUR SUBSCRIPTION
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What We Handle Section */}
      <section className="w-full bg-[#2a0f3a] relative overflow-hidden">
        <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 xl:px-20 py-12 sm:py-16 lg:py-24">
          
          {/* Header with Cow Illustration */}
          <div className="flex flex-col lg:flex-row justify-between items-start mb-10 sm:mb-12 lg:mb-16 gap-8 lg:gap-12">
            <div className="w-full lg:w-[55%]">
              <h2 className="text-[36px] sm:text-[56px] lg:text-[72px] font-bebas font-normal leading-[1.1] text-left text-white mb-2">
                What We Handle For You
              </h2>
              <h3 className="text-[36px] sm:text-[56px] lg:text-[72px] font-bebas font-normal leading-[1.1] text-left text-white -mt-2 sm:-mt-3 lg:-mt-4 mb-6 sm:mb-8">
                Every Month
              </h3>
              <p className="text-[14px] sm:text-[16px] lg:text-[18px] font-poppins font-normal leading-[1.6] text-left text-[#e4e4e4] max-w-[500px]">
                Purple Cow Unlimited replaces designers, editors, part time marketing staff, and unreliable freelancers — all in one simple subscription.
              </p>
              <p className="text-[12px] sm:text-[13px] lg:text-[14px] font-poppins font-normal leading-[1.6] text-left text-[#b8b8b8] max-w-[500px] mt-4">
                All services are delivered by a managed Purple Cow team of office based designers, developers, video editors, and marketing professionals who collaborate daily in one of our offices, Monday to Friday.
              </p>
            </div>
            
            {/* Cow Illustration */}
            <div className="w-full lg:w-[40%] flex justify-center lg:justify-end">
              <Image 
                src="/service-cow.png" 
                alt="Purple Cow illustration"
                width={400}
                height={320}
                className="w-[280px] sm:w-[320px] lg:w-[400px] h-auto object-contain"
              />
            </div>
          </div>

          {/* Service Categories Grid - Top Row (3 cards) */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 mb-4 sm:mb-5 lg:mb-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-64 sm:h-72 lg:h-80 bg-[#3f1651] animate-pulse rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 mb-4 sm:mb-5 lg:mb-6">
                
                {/* Creative and Design */}
                <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start bg-[#3f1651] rounded-xl p-5 sm:p-6 lg:p-7">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#8923b3] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                    </svg>
                  </div>
                  <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-left text-white uppercase">
                    Creative and Design
                  </h4>
                  <div className="flex flex-col gap-2.5 w-full">
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Social graphics</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Ad creatives</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Pitch decks</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Landing pages</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Thumbnails</span>
                    </div>
                  </div>
                </div>

                {/* Video and Motion */}
                <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start bg-[#3f1651] rounded-xl p-5 sm:p-6 lg:p-7">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#8923b3] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-left text-white uppercase">
                    Video and Motion
                  </h4>
                  <div className="flex flex-col gap-2.5 w-full">
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Short form edits</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Promo videos</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Basic motion graphics</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Repurposed clips</span>
                    </div>
                  </div>
                </div>

                {/* Web and Funnel */}
                <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start bg-[#3f1651] rounded-xl p-5 sm:p-6 lg:p-7">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#8923b3] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                  </div>
                  <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-left text-white uppercase">
                    Web and Funnel
                  </h4>
                  <div className="flex flex-col gap-2.5 w-full">
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Website updates</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Landing pages</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Lead capture flows</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Basic funnels</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Row (2 wider cards) */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 lg:gap-6 mb-10 sm:mb-12 lg:mb-16">
                
                {/* Social Presence and Content */}
                <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start bg-[#3f1651] rounded-xl p-5 sm:p-6 lg:p-7">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#8923b3] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                  </div>
                  <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-left text-white uppercase">
                    Social Presence and Content
                  </h4>
                  <div className="flex flex-col gap-2.5 w-full">
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Content calendars</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Captions</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Light community replies</span>
                    </div>
                  </div>
                </div>

                {/* Strategy and Support */}
                <div className="flex flex-col gap-4 sm:gap-5 justify-start items-start bg-[#3f1651] rounded-xl p-5 sm:p-6 lg:p-7">
                  <div className="w-11 h-11 sm:w-12 sm:h-12 bg-[#8923b3] rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 className="text-[18px] sm:text-[20px] lg:text-[22px] font-bebas font-normal leading-[1.2] text-left text-white uppercase">
                    Strategy and Support
                  </h4>
                  <div className="flex flex-col gap-2.5 w-full">
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Campaign ideas</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Message and positioning feedback</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[6px] h-[6px] bg-[#8923b3] rounded-full mr-3 flex-shrink-0"></div>
                      <span className="text-[13px] sm:text-[14px] lg:text-[15px] font-poppins font-normal leading-[1.5] text-[#e4e4e4]">Simple performance snapshots</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Bottom Text & CTA */}
          <div className="flex flex-col gap-6 sm:gap-8 justify-start items-start w-full">
            <p className="text-[14px] sm:text-[16px] lg:text-[18px] font-poppins font-normal leading-[1.5] text-left text-white">
              Everything we create belongs entirely to you.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
              <button
                onClick={handleGetStarted}
                className="w-full sm:w-auto px-8 py-4 bg-[#8923b3] text-white text-xs font-medium uppercase tracking-wider rounded-full hover:bg-[#a035d1] transition-colors"
              >
                GET STARTED WITH UNLIMITED REQUESTS
              </button>
              
              <a
                href="https://calendly.com/john-smulo/purple-cow-unlimited-discovery-call"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 bg-transparent text-[#8923b3] text-xs font-medium uppercase tracking-wider rounded-full border-2 border-[#8923b3] hover:bg-[#8923b3] hover:text-white transition-colors text-center"
              >
                TALK TO US FIRST
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
